import { defineRecipe } from '@chakra-ui/react';

/**
 * Button Component Theme
 *
 * This defines the styling for the Button component using Chakra's recipe pattern.
 * Recipes provide a way to define component variants and styles that automatically
 * integrate with the theme's semantic tokens.
 */
export const buttonRecipe = defineRecipe({
  base: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 10px',
    lineHeight: 1,
    height: 'auto',
    minHeight: 'unset',
    boxShadow: 'elevation.0',
    cursor: 'pointer',
    borderRadius: '4px',
    border: 'none',
    fontFamily: 'inherit',
    transition: 'filter 0.2s ease-in-out',
    _focus: {
      outline: 'none',
      filter: 'brightness(1.2)',
    },
    _hover: {
      filter: 'brightness(1.2)',
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: 0.6,
      _hover: {
        filter: 'none',
      },
    },
  },
  variants: {
    flavor: {
      primary: {
        bg: 'primary',
        color: 'lightText',
      },
      secondary: {
        bg: 'secondaryAccent',
        color: 'text',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        _hover: {
          filter: 'brightness(1.2)',
        },
      },
      accent: {
        bg: 'accent',
        color: 'text',
      },
      warning: {
        bg: 'warningAccent',
        color: 'darkText',
      },
      danger: {
        bg: 'danger',
        color: 'lightText',
      },
      none: {
        bg: 'transparent',
        color: 'text',
      },
    },
    buttonType: {
      addMetric: {
        bg: 'primary',
        color: 'lightText',
        borderRadius: '4px',
        padding: '6px 10px',
        _hover: {
          bg: 'primary',
          color: 'lightText',
        },
        _disabled: {
          _hover: {
            filter: 'none',
          },
        },
      },
      save: {
        bg: 'primary',
        color: 'lightText',
        borderRadius: '4px',
        padding: '6px 10px',
        _hover: {
          bg: 'primary',
          color: 'lightText',
        },
        _disabled: {
          _hover: {
            filter: 'none',
          },
        },
      },
      settings: {
        bg: 'primary',
        color: 'lightText',
        borderRadius: '4px',
        padding: '6px 10px',
        _hover: {
          bg: 'primary',
          color: 'lightText',
        },
        _disabled: {
          _hover: {
            filter: 'none',
          },
        },
      },
      selectAll: {
        bg: 'transparent',
        color: 'lightText',
        borderRadius: '4px',
        padding: '3px 5px',
        margin: 'auto 5px',
        textTransform: 'none',
        _hover: {
          bg: 'rgba(255, 255, 255, 0.1)',
        },
        _disabled: {
          boxShadow: 'none',
          opacity: 0.65,
          cursor: 'not-allowed',
          color: 'grey',
          bg: 'transparent',
          _hover: {
            bg: 'transparent',
            filter: 'none',
          },
        },
      },
      clearAll: {
        bg: 'transparent',
        color: 'lightText',
        borderRadius: '4px',
        padding: '3px 5px',
        margin: 'auto 5px',
        textTransform: 'none',
        _hover: {
          bg: 'rgba(255, 255, 255, 0.1)',
        },
        _disabled: {
          boxShadow: 'none',
          opacity: 0.65,
          cursor: 'not-allowed',
          color: 'grey',
          bg: 'transparent',
          _hover: {
            bg: 'transparent',
            filter: 'none',
          },
        },
      },
      toolbar: {
        bg: 'transparent',
        color: 'lightText',
        borderRadius: '4px',
        padding: '6px 10px',
        textTransform: 'none',
        _hover: {
          bg: 'primary',
          filter: 'brightness(1.2)',
        },
        _disabled: {
          boxShadow: 'none',
          opacity: 0.65,
          cursor: 'not-allowed',
          _hover: {
            bg: 'transparent',
            filter: 'none',
          },
        },
      },
    },

    spacing: {
      normal: {
        '&:not(:first-child)': {
          marginLeft: '10px',
        },
      },
      none: {},
    },
  },
  defaultVariants: {
    flavor: 'primary',
    spacing: 'normal',
  },
});

/**
 * IconButton Component Theme
 *
 * Specialized recipe for icon-only buttons with circular or rounded styling
 */
export const iconButtonRecipe = defineRecipe({
  base: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
    bg: 'transparent',
    transition: 'filter 0.2s ease-in-out, background 0.2s ease-in-out',
    color: 'text',
    border: 'none',
    height: 'auto',
    minHeight: 'unset',
    minWidth: 'unset',
    fontWeight: 'normal',
    _hover: {
      bg: 'primary',
      filter: 'brightness(1.2)',
    },
    _active: {
      outline: 'none',
      filter: 'brightness(1.2)',
    },
    _focus: {
      outline: 'none',
      filter: 'brightness(1.2)',
    },
    _disabled: {
      color: 'disabledText',
      cursor: 'not-allowed',
      _hover: {
        filter: 'none',
      },
    },
  },
  variants: {
    hasLabel: {
      true: {
        borderRadius: '5px',
      },
      false: {
        borderRadius: '50%',
      },
    },
    outline: {
      true: {
        bg: 'surface0',
      },
      false: {
        bg: 'transparent',
      },
    },
    buttonType: {
      default: {
        _hover: {
          bg: 'primary',
        },
      },
      add: {
        bg: 'primary',
        color: 'lightText',
        borderRadius: '4px',
        padding: '6px',
        _hover: {
          bg: 'primary',
          color: 'lightText',
        },
        _disabled: {
          _hover: {
            filter: 'none',
          },
        },
      },
      close: {
        color: 'warningAccent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'warningAccent',
        borderRadius: '4px',
        padding: '4px',
        _hover: {
          color: 'darkText',
          bg: 'warningAccent',
        },
        _disabled: {
          _hover: {
            color: 'warningAccent',
            bg: 'transparent',
          },
        },
      },
    },
  },
  defaultVariants: {
    hasLabel: false,
    outline: false,
    buttonType: 'default',
  },
});
